import { axiosInstance } from '../clients/axiosClient';
import { SUPPLEMENTS_API_PREFIX, API_V1_PREFIX } from '../utils/constants';

// Supplement interface based on the actual API response
export interface Supplement {
  supplement_id?: number;
  id?:number;
  supplement_name: string;
  description: string;
  instructions_for_taking?: string;
  image?: string | null;
  recommended_dosage?: number;
  dosage_units?: string;
  interactions_with_medications?: string;
  biomarker_ids?: number[];
  created_at?: string;
  updated_at?: string;
}

// API Response interfaces
export interface ListSupplementsResponse {
  supplements: Supplement[];
  total_count?: number;
  limit?: number;
  offset?: number;
}

export interface CreateSupplementRequest {
  supplement_name: string;
  description: string;
  image: string;
  instructions_for_taking: string;
  recommended_dosage: number;
  dosage_units: string;
  interactions_with_medications: string;
  biomarker_ids: number[];
}

export interface CreateSupplementResponse {
  supplement_id: number;
  status: string;
  message?: string;
}

export interface UpdateSupplementRequest {
  supplement_id: number;
  supplement_name?: string;
  description?: string;
  image?: string;
  instructions_for_taking?: string;
  recommended_dosage?: number;
  dosage_units?: string;
  interactions_with_medications?: string;
  biomarker_ids?: number[];
}

export interface UpdateSupplementResponse {
  supplement_id: number;
  status: string;
  message?: string;
}

export interface DeleteSupplementResponse {
  id: number;
  status: string;
}

// Member Supplement interfaces for the new API structure
export interface MemberSupplementRequest {
  member_id: string;
  supplement_id: number;
  dosage: number;
  meal_relation: string;
  time_of_day: string[];
  instructions_for_taking: string;
  additional_notes: string;
}

export interface MemberSupplementResponse {
  member_id: string;
  supplement_id: number;
  dosage: number;
  meal_relation: string;
  time_of_day: string[];
  instructions_for_taking: string;
  additional_notes: string;
}

// API Functions
export const getSupplementsList = async (
  search?: string,
  sort_by?: string,
  sort_direction?: string,
  limit: number = 10,
  offset: number = 0
): Promise<ListSupplementsResponse> => {
  try {
    const params: any = { limit, offset };
    if (search) params.search = search;
    if (sort_by) params.sort_by = sort_by;
    if (sort_direction) params.sort_direction = sort_direction;

    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${SUPPLEMENTS_API_PREFIX}`,
      { params }
    );
    return data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.message || error?.message || 'Failed to fetch supplements');
  }
};

export const getAllSupplements = async (
  search?: string,
  sort_by?: string,
  sort_direction?: string
): Promise<ListSupplementsResponse> => {
  try {
    const allSupplements: Supplement[] = [];
    let offset = 0;
    const limit = 100; 
    let hasMore = true;

    while (hasMore) {
      const response = await getSupplementsList(search, sort_by, sort_direction, limit, offset);
      const supplements = response.supplements || [];
      
      allSupplements.push(...supplements);
      
      hasMore = supplements.length === limit;
      offset += limit;
    }

    return {
      supplements: allSupplements,
      total_count: allSupplements.length
    };
  } catch (error: any) {
    throw new Error(error?.response?.data?.message || error?.message || 'Failed to fetch all supplements');
  }
};

export const createSupplement = async (
  newSupplementRequestObject: CreateSupplementRequest | FormData
): Promise<CreateSupplementResponse> => {
  try {
    let headers = {};
    if (!(newSupplementRequestObject instanceof FormData)) {
      headers = { 'Content-Type': 'application/json' };
    }
    
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}${SUPPLEMENTS_API_PREFIX}`,
      newSupplementRequestObject,
      { headers }
    );
    return data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error?.response?.data?.message || error?.message || 'Failed to create supplement');
  }
};

export const updateSupplement = async (
  supplementRequestObject: UpdateSupplementRequest
): Promise<UpdateSupplementResponse> => {
  try {
    const { supplement_id, ...updateData } = supplementRequestObject;
    const updatePayload = { ...updateData, supplement_id };
    const { data } = await axiosInstance.patch(
      `${API_V1_PREFIX}${SUPPLEMENTS_API_PREFIX}${supplement_id}/`,
      updatePayload
    );
    return data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.message || error?.message || 'Failed to update supplement');
  }
};

export const deleteSupplement = async (supplement_id: number): Promise<DeleteSupplementResponse> => {
  try {
    const { data } = await axiosInstance.delete(
      `${API_V1_PREFIX}${SUPPLEMENTS_API_PREFIX}${supplement_id}/`
    );
    return data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.message || error?.message || 'Failed to delete supplement');
  }
};

export const getSupplementById = async (supplement_id: number): Promise<Supplement> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${SUPPLEMENTS_API_PREFIX}${supplement_id}/`
    );
    return data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.message || error?.message || 'Failed to fetch supplement');
  }
};

export const uploadSupplementImage = async (file: File): Promise<{ image_url: string }> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}file-storage/photo`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return { image_url: data.url || data.image_url || data.file_url || '' };
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error?.response?.data?.message || error?.message || 'Failed to upload image');
  }
};

export const getPublicSupplementsList = async (
  limit: number = 10,
  offset: number = 0
): Promise<ListSupplementsResponse> => {
  try {
    const params = { limit, offset };
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}supplements/public/list_supplements`,
      { params }
    );
    return data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.message || error?.message || 'Failed to fetch public supplements');
  }
};

// Create member supplement
export const createMemberSupplement = async (
  memberSupplementRequest: MemberSupplementRequest
): Promise<MemberSupplementResponse> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}supplement-regimes/`,
      memberSupplementRequest,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error?.response?.data?.message || error?.message || 'Failed to create member supplement');
  }
};
